import { type FC, useMemo, useState } from "react";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import {
  PageTitle,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  Container,
  LoadingWrap,
  TopLine,
} from "../dashboard/sharedTableStyles";
import FixedPriceProfilesCards from "./components/FixedPriceProfilesCards";
import FixedMonthlyPlanProfilesCards from "./components/FixedMonthlyPlanProfilesCards";
import PriceCatalogueFiltersBar from "./components/PriceCatalogueFiltersBar";
import TieredPriceProfilesTable from "./components/TieredPriceProfilesTable";
import { useGetPriceCatalogue } from "./hooks/useGetPriceCatalogue";
import {
  EMPTY_PRICE_CATALOGUE_FILTERS,
  type PriceCatalogueFilters,
} from "./types";

const SectionTitle = styled(Typography)`
  margin: 8px 0 4px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #5a6675;
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
`;

const PriceCataloguePage: FC = () => {
  const [filters, setFilters] = useState<PriceCatalogueFilters>(
    EMPTY_PRICE_CATALOGUE_FILTERS
  );
  const { profiles, loading, error } = useGetPriceCatalogue(filters);

  const fixedProfiles = useMemo(
    () => profiles.filter((profile) => profile.pricingType === "fixed"),
    [profiles]
  );
  const tieredProfiles = useMemo(
    () => profiles.filter((profile) => profile.pricingType === "usage_tiered"),
    [profiles]
  );
  const fixedMonthlyPlanProfiles = useMemo(
    () =>
      profiles.filter(
        (profile) => profile.pricingType === "fixed_monthly_plan"
      ),
    [profiles]
  );

  return (
    <AnimateAppearanceWrapper>
      <Container>
        <TopLine>
          <PageTitle title="Price catalogue" />
        </TopLine>

        <PriceCatalogueFiltersBar filters={filters} onChange={setFilters} />

        {loading ? (
          <LoadingWrap>Loading catalogue…</LoadingWrap>
        ) : error ? (
          <EmptyState>{error}</EmptyState>
        ) : profiles.length === 0 ? (
          <EmptyState>No price profiles match these filters.</EmptyState>
        ) : (
          <>
            {fixedProfiles.length > 0 && (
              <>
                <SectionTitle variant={TypographyVariant.HEADER2}>
                  Fixed-price profiles
                </SectionTitle>
                <FixedPriceProfilesCards profiles={fixedProfiles} />
              </>
            )}
            {fixedMonthlyPlanProfiles.length > 0 && (
              <>
                <SectionTitle variant={TypographyVariant.HEADER2}>
                  Fixed monthly plan profiles
                </SectionTitle>
                <FixedMonthlyPlanProfilesCards
                  profiles={fixedMonthlyPlanProfiles}
                />
              </>
            )}
            <TieredPriceProfilesTable profiles={tieredProfiles} />
          </>
        )}
      </Container>
    </AnimateAppearanceWrapper>
  );
};

export default PriceCataloguePage;
